import { useCallback, useEffect, useMemo, useState } from 'react'
import type { FormEvent } from 'react'

import { canEdit } from '../../lib/nav'
import { Pager } from '../../lib/Pager'
import { SortTh, useSort, sortRows } from '../../lib/Sort'
import { coordText, errorText, mapsUrl, useOffsite } from '../../lib/offsite'
import type { OffsiteSite } from '../../lib/offsite'
import { useAuthedSession } from '../../lib/SessionContext'
import { SectionHead, ViewerNote } from './parts'

/**
 * ความแม่นยำที่ยอมรับได้ตอนกด "ใช้ตำแหน่งเครื่องนี้"
 * แย่กว่านี้แล้วรัศมีเริ่มต้น 30 ม. จะกลายเป็นการเสี่ยงดวง — พิกัดคลาดไป 100 ม.
 * เท่ากับคนที่ยืนอยู่หน้าออฟฟิศจริงลงเวลาไม่ได้ แล้วไม่มีใครรู้ว่าเพราะพิกัดผิด
 */
const ACCURACY_WARN_M = 50

/** ค่าเริ่มต้นเดียวกับ DF_lcs_radius ใน 005_offsite_checkin.sql */
const DEFAULT_RADIUS = 30

export default function OffsiteSites() {
  const session = useAuthedSession()
  const offsite = useOffsite()
  const writable = canEdit(session)

  const [sites, setSites] = useState<OffsiteSite[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)

  /** 'new' = ฟอร์มเพิ่ม · object = ฟอร์มแก้สถานที่นั้น · null = ปิดฟอร์ม */
  const [form, setForm] = useState<OffsiteSite | 'new' | null>(null)
  const [busyId, setBusyId] = useState<number | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  /* ⚠️ แบ่งหน้าที่ฝั่งนี้ ไม่ใช่ที่ API — /sites ต้องคืนครบทุกแห่งเพราะหน้าใบอนุญาต
     และหน้าช่วงเวลาเอารายการเดียวกันไปทำ dropdown และ map id→ชื่อ ถ้า API ส่งมา
     แค่หน้าแรก dropdown จะขาดตัวเลือกและชื่อสถานที่จะกลายเป็น "#3" แบบเงียบ ๆ
     (ข้อยกเว้นนี้เขียนไว้ในกติกาที่ backend/src/lib/paging.js) */
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  /* ⚠️ ตารางนี้เรียงที่ **หน้าจอ** ต่างจากทุกตารางอื่นในระบบ — เพราะ GET /sites
     ตั้งใจไม่แบ่งหน้าที่ API (หน้าอื่นเอารายการเดียวกันไปทำ dropdown ต้องได้ครบ)
     ข้อมูลหลักสิบแถวจึงเรียงในเบราว์เซอร์ได้โดยไม่มีปัญหาเรื่อง "เรียงแค่หน้านี้" */
  const sort = useSort([{ key: 'code', dir: 'asc' }], () => setPage(1))
  const [actionError, setActionError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setSites(await offsite.listSites())
      setLoadError(null)
    } catch (err) {
      setLoadError(errorText(err))
    } finally {
      setLoading(false)
    }
  }, [offsite])

  useEffect(() => {
    void load()
  }, [load])

  async function toggle(site: OffsiteSite) {
    const enable = site.status !== 'active'
    setBusyId(site.id)
    setActionError(null)
    setNotice(null)
    try {
      await offsite.setSiteEnabled(site.id, enable)
      setNotice(`${enable ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}สถานที่ ${site.code} แล้ว`)
      await load()
    } catch (err) {
      setActionError(errorText(err))
    } finally {
      setBusyId(null)
    }
  }

  const activeHead = sites?.find((s) => s.status === 'active' && Boolean(s.isHeadOffice)) ?? null

  const sorted = useMemo(
    () =>
      sortRows(sites ?? [], sort.sort, {
        code: (x) => x.code,
        name: (x) => x.name,
        radiusM: (x) => x.radiusM,
        isHeadOffice: (x) => Boolean(x.isHeadOffice),
        status: (x) => x.status,
      }),
    [sites, sort.sort],
  )

  return (
    <>
      <h1>สถานที่ลงเวลา</h1>
      <p className="meta">
        พิกัดและรัศมีของที่ที่พนักงานลงเวลาผ่าน LINE ได้ · ใบอนุญาตแบบ “ในรัศมีสถานที่”
        และการเปิดลงเวลาชั่วคราวใช้ขอบเขตจากตารางนี้
      </p>

      {!writable && <ViewerNote />}
      {loadError && <p className="error">โหลดรายการไม่สำเร็จ — {loadError}</p>}
      {actionError && <p className="error">{actionError}</p>}
      {notice && <p className="ok">{notice}</p>}

      {/* คำเตือนสองอันนี้มาจากกติกา fail-closed ในหัวไฟล์ 005_offsite_checkin.sql
          ถ้าไม่บอกไว้ คนตั้งค่าจะไม่รู้ว่า "ตารางว่าง = ไม่มีใครลงเวลาได้เลย" */}
      {sites && sites.length === 0 && (
        <p className="warn">
          ยังไม่มีสถานที่ในระบบ — ระบบตั้งใจให้ <strong>ปิดไว้ก่อน</strong> ตารางนี้ว่างเท่ากับ
          ยังไม่มีใครลงเวลาผ่าน LINE ได้เลย ต้องเพิ่มสถานที่ก่อน แล้วจึงออกใบอนุญาตให้พนักงาน
        </p>
      )}
      {sites && sites.length > 0 && !activeHead && (
        <p className="warn">
          ยังไม่ได้กำหนด <strong>สำนักงานใหญ่</strong> — เมนู “เปิดลงเวลาชั่วคราว” แบบไม่ระบุสถานที่
          จะหาขอบเขตไม่ได้ ให้ติ๊กช่อง “สำนักงานใหญ่” ที่สถานที่หลักหนึ่งแห่ง
        </p>
      )}

      {writable && form === null && (
        <p>
          <button type="button" onClick={() => setForm('new')}>
            เพิ่มสถานที่
          </button>
        </p>
      )}

      {writable && form !== null && (
        <SiteForm
          // key บังคับให้ state ในฟอร์มเริ่มใหม่เมื่อสลับไปแก้อีกสถานที่
          key={form === 'new' ? 'new' : form.id}
          site={form === 'new' ? null : form}
          sites={sites ?? []}
          onSaved={(message) => {
            setForm(null)
            setNotice(message)
            setActionError(null)
            void load()
          }}
          onCancel={() => setForm(null)}
        />
      )}

      <section className="card">
        <SectionHead
          title="รายการสถานที่"
          meta={sites ? `ทั้งหมด ${sites.length} แห่ง` : undefined}
          onReload={() => void load()}
          loading={loading}
        />

        {sites === null ? (
          <p className="meta">กำลังโหลด…</p>
        ) : sites.length === 0 ? (
          <p className="meta">ยังไม่มีข้อมูล</p>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <SortTh label="รหัส" sortKey="code" sort={sort.sort} onToggle={sort.toggle} />
                  <SortTh label="ชื่อ" sortKey="name" sort={sort.sort} onToggle={sort.toggle} />
                  {/* พิกัดเป็นคู่ lat/lng เรียงแล้วไม่มีความหมายให้คนอ่าน */}
                  <th className="nowrap">พิกัด</th>
                  <SortTh label="รัศมี" sortKey="radiusM" sort={sort.sort} onToggle={sort.toggle} className="num" />
                  <SortTh label="สำนักงานใหญ่" sortKey="isHeadOffice" sort={sort.sort} onToggle={sort.toggle} />
                  <SortTh label="สถานะ" sortKey="status" sort={sort.sort} onToggle={sort.toggle} />
                  {writable && <th>จัดการ</th>}
                </tr>
              </thead>
              <tbody>
                {sorted.slice((page - 1) * pageSize, page * pageSize).map((site) => {
                  const disabled = site.status !== 'active'
                  return (
                    <tr key={site.id} className={disabled ? 'off' : undefined}>
                      <td>
                        <code>{site.code}</code>
                      </td>
                      <td>
                        {site.name}
                        {site.note && <div className="meta">{site.note}</div>}
                      </td>
                      <td className="nowrap">
                        <a href={mapsUrl(site.lat, site.lng)} target="_blank" rel="noreferrer">
                          {coordText(site.lat)}, {coordText(site.lng)}
                        </a>
                      </td>
                      <td className="num">{site.radiusM} ม.</td>
                      <td>{Boolean(site.isHeadOffice) ? <span className="chip">สำนักงานใหญ่</span> : '-'}</td>
                      <td>
                        <span className={disabled ? 'chip chip--off' : 'chip chip--ok'}>
                          {disabled ? 'ปิดใช้งาน' : 'ใช้งาน'}
                        </span>
                      </td>
                      {writable && (
                        <td>
                          <div className="row-actions">
                            <button
                              type="button"
                              className="ghost tiny"
                              onClick={() => setForm(site)}
                              disabled={busyId === site.id}
                            >
                              แก้ไข
                            </button>
                            <button
                              type="button"
                              className="ghost tiny"
                              onClick={() => void toggle(site)}
                              disabled={busyId === site.id}
                            >
                              {busyId === site.id ? 'กำลังบันทึก…' : disabled ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        <Pager
          page={page}
          pageSize={pageSize}
          /* sites เป็น null ตอนยังโหลดไม่เสร็จ — แถบยังต้องวาดไว้ ไม่ให้หน้ากระตุกตอนโหลดจบ */
          total={sites?.length ?? 0}
          loading={loading}
          onPage={setPage}
          unit="แห่ง"
          onPageSize={(n) => {
            setPageSize(n)
            setPage(1)
          }}
        />
      </section>
    </>
  )
}

function geoMessage(err: GeolocationPositionError): string {
  if (err.code === err.PERMISSION_DENIED) {
    return 'เบราว์เซอร์ไม่อนุญาตให้อ่านตำแหน่ง — กดอนุญาตที่ไอคอนซ้ายแถบที่อยู่ (และหน้านี้ต้องเปิดผ่าน https หรือ localhost ไม่งั้นเบราว์เซอร์ปิดฟีเจอร์นี้ทั้งหมด)'
  }
  if (err.code === err.POSITION_UNAVAILABLE) {
    return 'อ่านตำแหน่งไม่ได้ — เครื่องนี้อาจไม่มีตัวช่วยระบุตำแหน่ง ลองเปิดหน้านี้บนมือถือที่จุดนั้นแทน'
  }
  if (err.code === err.TIMEOUT) return 'อ่านตำแหน่งไม่ทันใน 15 วินาที ลองกดอีกครั้ง'
  return err.message || 'อ่านตำแหน่งไม่สำเร็จ'
}

function SiteForm({
  site,
  sites,
  onSaved,
  onCancel,
}: {
  /** null = เพิ่มใหม่ */
  site: OffsiteSite | null
  /** ทั้งรายการ — ใช้เตือนว่าสำนักงานใหญ่มีที่อื่นอยู่แล้ว */
  sites: OffsiteSite[]
  onSaved: (message: string) => void
  onCancel: () => void
}) {
  const offsite = useOffsite()
  const editing = site !== null

  const [code, setCode] = useState(site?.code ?? '')
  const [name, setName] = useState(site?.name ?? '')
  const [lat, setLat] = useState(site ? coordText(site.lat) : '')
  const [lng, setLng] = useState(site ? coordText(site.lng) : '')
  const [radius, setRadius] = useState(String(site?.radiusM ?? DEFAULT_RADIUS))
  const [head, setHead] = useState(site ? Boolean(site.isHeadOffice) : false)
  const [note, setNote] = useState(site?.note ?? '')

  const [locating, setLocating] = useState(false)
  const [accuracy, setAccuracy] = useState<number | null>(null)
  const [geoError, setGeoError] = useState<string | null>(null)

  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const latNum = Number(lat)
  const lngNum = Number(lng)
  const radiusNum = Number(radius)

  /* เกณฑ์ทั้งชุดตรงกับ CHECK constraint ใน 005_offsite_checkin.sql — บอกผู้ใช้ตรงนี้
     จะได้ไม่ต้องรอ backend ตีกลับเป็นข้อความของ SQL ที่อ่านไม่รู้เรื่อง
     (backend ตรวจซ้ำอยู่ ห้ามเชื่อการตรวจฝั่งนี้เป็นด่านเดียว) */
  const trimmedCode = code.trim().toUpperCase()
  const codeBad = !editing && !/^[A-Za-z0-9_-]{1,32}$/.test(trimmedCode)
  const nameBad = name.trim().length === 0
  const latBad = lat.trim() === '' || !Number.isFinite(latNum) || latNum < -90 || latNum > 90
  const lngBad = lng.trim() === '' || !Number.isFinite(lngNum) || lngNum < -180 || lngNum > 180
  const zeroBad = !latBad && !lngBad && latNum === 0 && lngNum === 0
  const radiusBad = !Number.isInteger(radiusNum) || radiusNum < 10 || radiusNum > 5000

  const otherHead = sites.find(
    (s) => s.id !== site?.id && s.status === 'active' && Boolean(s.isHeadOffice),
  )
  const headConflict = head && otherHead !== undefined

  const canSubmit =
    !submitting && !codeBad && !nameBad && !latBad && !lngBad && !zeroBad && !radiusBad && !headConflict

  function useThisDevice() {
    if (!navigator.geolocation) {
      setGeoError('เบราว์เซอร์นี้อ่านตำแหน่งไม่ได้ — ต้องกรอกพิกัดเอง')
      return
    }
    setLocating(true)
    setGeoError(null)
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false)
        // ปัดเป็น 6 ตำแหน่งตรงนี้เลย เพราะคอลัมน์เป็น DECIMAL(9,6) — ถ้าไม่ปัด
        // ค่าที่โชว์บนฟอร์มจะไม่ตรงกับค่าที่ DB เก็บจริง แล้วดูเหมือนระบบแก้ค่าเอง
        setLat(pos.coords.latitude.toFixed(6))
        setLng(pos.coords.longitude.toFixed(6))
        setAccuracy(Number.isFinite(pos.coords.accuracy) ? Math.round(pos.coords.accuracy) : null)
      },
      (err) => {
        setLocating(false)
        setGeoError(geoMessage(err))
      },
      // maximumAge: 0 = ห้ามใช้ค่าที่ cache ไว้ คนกดปุ่มนี้เพราะยืนอยู่ที่จุดนั้น "ตอนนี้"
      { enableHighAccuracy: true, timeout: 15_000, maximumAge: 0 },
    )
  }

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (!canSubmit) return

    setSubmitting(true)
    setError(null)
    const body = {
      name: name.trim(),
      lat: Number(latNum.toFixed(6)),
      lng: Number(lngNum.toFixed(6)),
      radiusM: radiusNum,
      isHeadOffice: head,
      note: note.trim() === '' ? null : note.trim(),
    }
    try {
      if (site) {
        await offsite.updateSite(site.id, body)
        onSaved(`บันทึกสถานที่ ${site.code} แล้ว`)
      } else {
        await offsite.createSite({ code: trimmedCode, ...body })
        onSaved(`เพิ่มสถานที่ ${trimmedCode} แล้ว`)
      }
    } catch (err) {
      setError(errorText(err))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <section className="card form-card--wide">
      <h2>{editing ? `แก้ไขสถานที่ ${site.code}` : 'เพิ่มสถานที่'}</h2>
      <form onSubmit={submit}>
        <div className="form-row">
          <label>
            รหัส
            <input
              value={editing ? site.code : code}
              onChange={(e) => setCode(e.target.value)}
              maxLength={32}
              placeholder="HQ"
              // แก้รหัสหลังสร้างไม่ได้: แถวลงเวลาเก่าเก็บ site_code ไว้เป็น snapshot
              // เปลี่ยนแล้วรายงานย้อนหลังจะมีสองรหัสของที่เดียวกันแบบไม่มีอะไรโยงถึงกัน
              disabled={editing}
              required
            />
            {editing ? (
              <small className="hint">รหัสแก้ไม่ได้ — รายงานย้อนหลังอ้างรหัสนี้ไว้แล้ว</small>
            ) : codeBad && code.trim() !== '' ? (
              <small className="bad">ใช้ได้แต่ A-Z 0-9 - _ ยาวไม่เกิน 32 ตัว</small>
            ) : (
              // คอลัมน์ snapshot ในตารางลงเวลาเป็น VARCHAR (ไม่ใช่ NVARCHAR) ตัวอักษรไทย
              // จะกลายเป็น ? ในรายงาน จึงบังคับให้ใช้อักษรอังกฤษ/ตัวเลขเท่านั้น
              <small className="hint">อังกฤษ/ตัวเลขเท่านั้น เช่น HQ, WH1 (ระบบเก็บเป็นตัวใหญ่)</small>
            )}
          </label>

          <label>
            ชื่อสถานที่
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={200}
              placeholder="สำนักงานใหญ่"
              required
            />
            <small className="hint">ชื่อนี้จะไปโชว์บนหน้าลงเวลาของพนักงาน</small>
          </label>
        </div>

        <div className="form-row">
          <label>
            ละติจูด (lat)
            <input
              value={lat}
              onChange={(e) => setLat(e.target.value)}
              inputMode="decimal"
              placeholder="13.756331"
              required
            />
            {latBad && lat.trim() !== '' && <small className="bad">ต้องเป็นตัวเลข -90 ถึง 90</small>}
          </label>

          <label>
            ลองจิจูด (lng)
            <input
              value={lng}
              onChange={(e) => setLng(e.target.value)}
              inputMode="decimal"
              placeholder="100.501765"
              required
            />
            {lngBad && lng.trim() !== '' && <small className="bad">ต้องเป็นตัวเลข -180 ถึง 180</small>}
          </label>
        </div>

        {zeroBad && (
          <p className="error">พิกัด 0, 0 อยู่กลางทะเล — มักเกิดจากฟอร์มที่ส่งค่าว่าง ให้กรอกพิกัดจริง</p>
        )}

        <div className="row-actions">
          <button type="button" className="ghost" onClick={useThisDevice} disabled={locating}>
            {locating ? 'กำลังอ่านตำแหน่ง…' : 'ใช้ตำแหน่งเครื่องนี้'}
          </button>
          {!latBad && !lngBad && !zeroBad && (
            <a className="ghost" href={mapsUrl(latNum, lngNum)} target="_blank" rel="noreferrer">
              เปิดใน Google Maps
            </a>
          )}
        </div>
        <small className="hint">
          ยืนอยู่ที่จุดนั้นแล้วกด “ใช้ตำแหน่งเครื่องนี้” แม่นกว่าการไปคัดลอกพิกัดจาก Google Maps ·
          เดสก์ท็อปที่ไม่มี GPS จะได้ตำแหน่งจาก Wi-Fi/IP ซึ่งคลาดเคลื่อนได้เป็นกิโลเมตร
          ถ้าค่าความแม่นยำสูงให้เปิดหน้านี้บนมือถือที่จุดนั้นแทน
        </small>

        {geoError && <p className="error">{geoError}</p>}

        {accuracy !== null &&
          (accuracy > ACCURACY_WARN_M ? (
            <p className="warn">
              ได้ตำแหน่งมาแต่ความแม่นยำอยู่ที่ ±{accuracy} ม. ซึ่งแย่กว่า {ACCURACY_WARN_M} ม. —
              พิกัดนี้อาจไม่แม่นพอสำหรับรัศมี {DEFAULT_RADIUS} ม. (คนที่ยืนอยู่หน้าอาคารจริงอาจลงเวลาไม่ได้)
              ลองออกไปที่โปร่ง เปิด GPS แล้วกดอีกครั้ง หรือเพิ่มรัศมีให้กว้างขึ้น
            </p>
          ) : (
            <p className="ok">อ่านตำแหน่งได้ ความแม่นยำ ±{accuracy} ม. — ใช้กับรัศมี {radius || '-'} ม. ได้</p>
          ))}

        <div className="form-row">
          <label>
            รัศมีที่ยอมรับ (เมตร)
            <input
              value={radius}
              onChange={(e) => setRadius(e.target.value)}
              inputMode="numeric"
              required
            />
            {radiusBad ? (
              <small className="bad">ต้องเป็นจำนวนเต็ม 10–5000 ม.</small>
            ) : (
              <small className="hint">
                ออฟฟิศใช้ {DEFAULT_RADIUS} ม. · โกดัง/ไซต์งานพื้นที่กว้างให้ตั้งมากกว่านี้
              </small>
            )}
          </label>

          <label className="check">
            <input type="checkbox" checked={head} onChange={(e) => setHead(e.target.checked)} />
            เป็นสำนักงานใหญ่
          </label>
        </div>

        {headConflict && otherHead && (
          <p className="warn">
            ตอนนี้ <code>{otherHead.code}</code> ({otherHead.name}) เป็นสำนักงานใหญ่อยู่ —
            มีได้ที่เดียวต่อบริษัท ต้องปลดของเดิมก่อน (ฐานข้อมูลกันไว้อีกชั้น)
          </p>
        )}
        {!headConflict && head && (
          <small className="hint">
            สำนักงานใหญ่ = ขอบเขตที่ใช้เวลา “เปิดลงเวลาชั่วคราว” แบบไม่ระบุสถานที่
          </small>
        )}

        <label>
          หมายเหตุ
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={255}
            placeholder="เช่น อาคาร A ชั้น 1 ประตูหน้า"
          />
        </label>

        {error && <p className="error">{error}</p>}

        <div className="row-actions">
          <button type="submit" disabled={!canSubmit}>
            {submitting ? 'กำลังบันทึก…' : editing ? 'บันทึกการแก้ไข' : 'เพิ่มสถานที่'}
          </button>
          <button type="button" className="ghost" onClick={onCancel} disabled={submitting}>
            ยกเลิก
          </button>
        </div>
      </form>
    </section>
  )
}
